import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { canMoveSpec, moveSpecWithinBlock, setSpecConfirmed } from '@rock/enrichment';
import { EquipmentTypeDao } from '@rock/db-admin';
import { RefreshStatusBadge } from '@/components/badges/RefreshStatusBadge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Form } from '@/components/ui/form';
import { Switch } from '@/components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { AppInputField } from '@/form/FormUI';
import { equipmentTypeDraftStatusState, equipmentTypeEditState, equipmentTypeRefreshState } from '@/state';
import { useAsyncAtomValue, useAtomLoadable, useLoadableSetAtom } from '@/state/loadableHooks';
import { extractFieldErrors } from '@/util/extractFieldErrors';
import logger from '@/util/logger';
import { zodResolver } from '@hookform/resolvers/zod';
import { hasUnpublishedChanges } from '@rock/dao';
import { createId } from '@rock/db-admin';
import { foldSpecCode } from '@rock/enrichment';
import {
    type EquipmentSpec,
    EquipmentSpecSchema,
    type EquipmentType,
    type EquipmentTypeId,
    type RefreshId,
} from '@rock/types';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { type FC, useCallback, useEffect, useState } from 'react';
import { type FieldErrors, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import * as z from 'zod';

const log = logger('EquipmentSpecsTable');
const equipmentTypeDao = new EquipmentTypeDao();

/** The editable half of a spec. `confirmed` is toggled in the table, never through the form. */
const SpecFormSchema = EquipmentSpecSchema.pick({ code: true, label: true, description: true }).extend({
    label: z.string().min(1, 'A label is required'),
});
type SpecForm = z.infer<typeof SpecFormSchema>;

/**
 * The review surface for one equipment type's spec list — the rows every unit of the type shows on its
 * Specs tab, in this order, with these labels. Reads and writes the STAGED draft like the fields table
 * does (`equipmentTypeEditState`), so an expert's reorder / rename / confirm lands in the draft and goes
 * live with the same Publish as everything else on the type.
 *
 * Rows arrive as AI drafts (`confirmed: false`) from the generator or the Regenerate button; the human's
 * job is to confirm the ones they vouch for, fix the wording, drop the noise, and add what the model missed.
 * Confirming is not a visibility gate — every row reaches the Specs tab — it is what sorts a row above the
 * drafts, and the arrows reorder only within a block (`@rock/enrichment`'s `setSpecConfirmed` /
 * `moveSpecWithinBlock` own both rules, so the stored list always reads confirmed-first).
 */
const EquipmentSpecsTable: FC<{ typeId: EquipmentTypeId }> = ({ typeId }) => {
    const [loadable, updateEquipmentType] = useLoadableSetAtom(equipmentTypeEditState(typeId));
    const draftStatus = useAsyncAtomValue(equipmentTypeDraftStatusState(typeId), undefined);
    const equipmentType = loadable.state === 'hasData' ? loadable.data : undefined;

    if (!equipmentType) return null;

    const specs = equipmentType.specs ?? [];
    const confirmedCount = specs.filter((spec) => spec.confirmed).length;
    const hasDraft = hasUnpublishedChanges(draftStatus);

    return (
        <div className="space-y-5">
            <div>
                What homeowners see on the Specs tab for every unit of this type — in this order, with these labels.
                Every row shows; confirming one vouches for it and lifts it above the unreviewed drafts, so the specs
                you trust lead. Fix the wording, drop the noise, add what is missing, then Publish.
            </div>
            <div className="text-sm text-muted-foreground">
                {specs.length === 0 ? 'No specs yet.' : `${confirmedCount} of ${specs.length} confirmed`}
            </div>
            <SpecsEditor type={equipmentType} hasDraft={hasDraft} updateEquipmentType={updateEquipmentType} />
        </div>
    );
};

const SpecsEditor: FC<{
    type: EquipmentType;
    hasDraft: boolean;
    updateEquipmentType: (equipmentType: EquipmentType) => Promise<void>;
}> = ({ type, hasDraft, updateEquipmentType }) => {
    // `null` = adding a new spec; a code = editing that spec; `undefined` = dialog closed.
    const [editCode, setEditCode] = useState<string | null | undefined>(undefined);
    const [loading, setLoading] = useState(false);
    const specs = type.specs ?? [];
    const editing = specs.find((spec) => spec.code === editCode);

    /** Stage the list. Returns whether it landed, so dialog-driven callers know when to close. */
    async function writeSpecs(next: EquipmentSpec[], success: string, failure: string): Promise<boolean> {
        setLoading(true);
        try {
            await updateEquipmentType({ ...type, specs: next });
            toast.success(success);
            return true;
        } catch (err) {
            log.error(failure, { err, typeId: type.id });
            toast.error(failure);
            return false;
        } finally {
            setLoading(false);
        }
    }

    async function saveSpec(form: SpecForm) {
        if (editCode === null) {
            // Same canonical form the generator writes, compared folded: uniqueness on `code` is
            // load-bearing (row keys, edit lookup, the confirm toggle), and `Fuel_Type` beside `fuel-type`
            // would be one concept twice.
            const code = foldSpecCode(form.code);
            if (!code) {
                toast.error('The code needs at least one letter or digit');
                return;
            }
            if (specs.some((spec) => foldSpecCode(spec.code) === code)) {
                toast.error(`A spec with code "${code}" already exists`);
                return;
            }
            const added = await writeSpecs(
                [...specs, { ...form, code, confirmed: false }],
                'Added spec',
                'Error adding spec',
            );
            if (added) setEditCode(undefined);
            return;
        }
        // Editing keeps the row's confirmation: the person changing the wording is the reviewer.
        const next = specs.map((spec) => (spec.code === editCode ? { ...spec, ...form } : spec));
        if (await writeSpecs(next, 'Updated spec', 'Error updating spec')) setEditCode(undefined);
    }

    async function deleteSpec() {
        const next = specs.filter((spec) => spec.code !== editCode);
        if (await writeSpecs(next, 'Removed spec', 'Error removing spec')) setEditCode(undefined);
    }

    // Confirming moves the row across the seam (last confirmed / first draft) — see `setSpecConfirmed`.
    function toggleConfirmed(code: string, confirmed: boolean) {
        return writeSpecs(
            setSpecConfirmed(specs, code, confirmed),
            confirmed ? 'Confirmed' : 'Unconfirmed',
            'Error updating spec',
        );
    }

    // Arrows reorder within a row's own block only; the confirm toggle is what crosses the seam.
    function move(code: string, delta: -1 | 1) {
        return writeSpecs(moveSpecWithinBlock(specs, code, delta), 'Reordered', 'Error reordering specs');
    }

    return (
        <div className="space-y-2">
            <div className="flex flex-row justify-end gap-2">
                <RegenerateButton typeId={type.id} hasDraft={hasDraft} />
                <Button onClick={() => setEditCode(null)} variant="outline" size="sm" className="cursor-pointer">
                    Add Spec
                </Button>
            </div>
            <SpecsRows
                specs={specs}
                loading={loading}
                onEdit={(spec) => setEditCode(spec.code)}
                onToggle={toggleConfirmed}
                onMove={move}
            />
            {editCode !== undefined && (
                <EditSpecForm
                    loading={loading}
                    spec={editing}
                    onSave={saveSpec}
                    onDelete={deleteSpec}
                    onCancel={() => setEditCode(undefined)}
                />
            )}
        </div>
    );
};

const SpecsRows: FC<{
    specs: EquipmentSpec[];
    loading: boolean;
    onEdit: (spec: EquipmentSpec) => void;
    onToggle: (code: string, confirmed: boolean) => void;
    onMove: (code: string, delta: -1 | 1) => void;
}> = ({ specs, loading, onEdit, onToggle, onMove }) => (
    <div className="px-4 pb-4">
        <Table className="not-prose">
            <TableHeader className="bg-secondary">
                <TableRow>
                    <TableHead className="w-16" />
                    <TableHead className="w-24">Confirmed</TableHead>
                    <TableHead>Code</TableHead>
                    <TableHead>Label</TableHead>
                    <TableHead>Description</TableHead>
                </TableRow>
            </TableHeader>
            <TableBody>
                {specs.map((spec) => (
                    <TableRow
                        key={spec.code}
                        onClick={() => onEdit(spec)}
                        className={spec.confirmed ? 'cursor-pointer' : 'cursor-pointer text-muted-foreground'}
                    >
                        <TableCell onClick={(event) => event.stopPropagation()}>
                            <div className="flex flex-col">
                                <button
                                    type="button"
                                    className="cursor-pointer disabled:opacity-30"
                                    disabled={loading || !canMoveSpec(specs, spec.code, -1)}
                                    onClick={() => onMove(spec.code, -1)}
                                    aria-label={`Move ${spec.label} up`}
                                >
                                    <ChevronUp size={14} />
                                </button>
                                <button
                                    type="button"
                                    className="cursor-pointer disabled:opacity-30"
                                    disabled={loading || !canMoveSpec(specs, spec.code, 1)}
                                    onClick={() => onMove(spec.code, 1)}
                                    aria-label={`Move ${spec.label} down`}
                                >
                                    <ChevronDown size={14} />
                                </button>
                            </div>
                        </TableCell>
                        <TableCell onClick={(event) => event.stopPropagation()}>
                            <Switch
                                checked={spec.confirmed}
                                disabled={loading}
                                onCheckedChange={(checked) => onToggle(spec.code, checked)}
                                aria-label={`Confirm ${spec.label}`}
                            />
                        </TableCell>
                        <TableCell className="font-mono text-xs">{spec.code}</TableCell>
                        <TableCell>{spec.label}</TableCell>
                        <TableCell className="whitespace-normal">{spec.description}</TableCell>
                    </TableRow>
                ))}
                {specs.length === 0 && (
                    <TableRow>
                        <TableCell colSpan={5}>
                            No specs yet — Regenerate drafts a list, or Add Spec starts one by hand.
                        </TableCell>
                    </TableRow>
                )}
            </TableBody>
        </Table>
    </div>
);

/**
 * Arms the server-side regenerate (`refreshEquipmentTypeSpecs`) by creating a `_refresh` doc. That
 * function writes the LIVE type doc and preserves confirmed rows it finds THERE — so while an unpublished
 * draft exists, a regenerate would land on live, be invisible here (the editor shows the draft), and be
 * overwritten by the next Publish. Disabled in that state rather than left to confuse.
 */
const RegenerateButton: FC<{ typeId: EquipmentTypeId; hasDraft: boolean }> = ({ typeId, hasDraft }) => {
    const [refreshId, setRefreshId] = useState<RefreshId>();
    const refreshLoadable = useAtomLoadable(equipmentTypeRefreshState({ typeId, refreshId }));

    useEffect(() => {
        if (refreshId && refreshLoadable.state === 'hasData' && !refreshLoadable.data) {
            void equipmentTypeDao
                .requestEquipmentTypeRefresh(typeId, {
                    object: 'task-refresh',
                    schema: 1,
                    id: refreshId,
                    type: 'specs',
                    status: 'requested',
                })
                // db-admin awaits its writes, so a rejection here would otherwise be unhandled and
                // leave the button sitting on a refresh that never started.
                .catch((err) => log.error('Failed to request a specs refresh', { err, typeId }));
        }
    }, [typeId, refreshId, refreshLoadable]);

    if (refreshLoadable.state === 'hasData' && refreshLoadable.data) {
        return <RefreshStatusBadge status={refreshLoadable.data.status} />;
    }

    const button = (
        <Button
            onClick={() => setRefreshId(createId() as RefreshId)}
            variant="outline"
            size="sm"
            className="cursor-pointer"
            disabled={hasDraft}
        >
            Regenerate Drafts
        </Button>
    );
    if (!hasDraft) return button;
    return (
        <Tooltip>
            <TooltipTrigger asChild>
                <span>{button}</span>
            </TooltipTrigger>
            <TooltipContent className="max-w-sm">
                Publish or discard the unpublished changes first — a regenerate writes the live list.
            </TooltipContent>
        </Tooltip>
    );
};

const EditSpecForm: FC<{
    loading: boolean;
    spec?: EquipmentSpec;
    onSave: (spec: SpecForm) => void;
    onDelete: () => void;
    onCancel: () => void;
}> = ({ loading, spec, onSave, onDelete, onCancel }) => {
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const form = useForm<SpecForm>({
        resolver: zodResolver(SpecFormSchema),
        defaultValues: spec ?? { code: '', label: '', description: '' },
    });

    const onInvalid = useCallback((errors: FieldErrors) => {
        toast.error('Validation Errors', { description: extractFieldErrors(errors).join(', ') });
    }, []);

    return (
        <Dialog open onOpenChange={onCancel}>
            <DialogContent className="max-h-[80vh] overflow-y-auto">
                <Form {...form}>
                    <form onSubmit={form.handleSubmit(onSave, onInvalid)}>
                        <DialogHeader>
                            <DialogTitle>{spec?.label || 'Add Spec'}</DialogTitle>
                        </DialogHeader>
                        <div className="flex flex-col gap-4 items-stretch p-4">
                            <AppInputField
                                label="Code"
                                placeholder="e.g. fuel-source (not editable after create)"
                                control={form.control}
                                name="code"
                                disabled={!!spec}
                            />
                            <AppInputField
                                label="Label"
                                placeholder="What the homeowner sees, with the unit — e.g. Heating Capacity (BTU/hr)"
                                control={form.control}
                                name="label"
                            />
                            <AppInputField
                                label="Description"
                                placeholder="What the value looks like and where a manual states it"
                                control={form.control}
                                name="description"
                            />
                        </div>
                        <DialogFooter className="flex flex-row items-center justify-between sm:justify-between">
                            {spec ? (
                                <Button
                                    type="button"
                                    variant="destructive"
                                    onClick={() => setShowDeleteConfirm(true)}
                                    disabled={loading}
                                >
                                    Remove
                                </Button>
                            ) : (
                                <div />
                            )}
                            <div className="flex flex-row items-center gap-2">
                                <Button type="button" variant="secondary" onClick={onCancel} disabled={loading}>
                                    Cancel
                                </Button>
                                <Button type="submit" disabled={loading}>
                                    {spec ? 'Update Spec' : 'Add Spec'}
                                </Button>
                            </div>
                        </DialogFooter>
                    </form>
                </Form>
            </DialogContent>

            <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Remove "{spec?.label}"?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Homeowners stop seeing this row on every unit of this type once published.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel className="cursor-pointer" disabled={loading}>
                            Cancel
                        </AlertDialogCancel>
                        <AlertDialogAction variant="destructive" onClick={onDelete} disabled={loading}>
                            Remove
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </Dialog>
    );
};

export default EquipmentSpecsTable;
